import React, { useState, useEffect, useCallback } from 'react';
import { PipelineBuild, PipelineStage, PipelineStageStatus } from './types';
import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';
import './PipelineModal.css';

interface PipelineModalProps {
  prNumber: number;
  prTitle: string;
  onClose: () => void;
}

function formatDuration(ms: number): string {
  if (ms <= 0) return '—';
  const totalSecs = Math.floor(ms / 1000);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}

function formatElapsed(startMs: number): string {
  if (startMs <= 0) return '—';
  const elapsed = Date.now() - startMs;
  return formatDuration(elapsed);
}

const STATUS_META: Record<PipelineStageStatus, { icon: string; label: string; className: string }> = {
  SUCCESS:      { icon: '✓', label: 'Success',     className: 'stage-success' },
  FAILED:       { icon: '✗', label: 'Failed',      className: 'stage-failed' },
  UNSTABLE:     { icon: '⚠', label: 'Unstable',    className: 'stage-unstable' },
  IN_PROGRESS:  { icon: '⟳', label: 'Running',     className: 'stage-running' },
  PAUSED:       { icon: '⏸', label: 'Paused',      className: 'stage-paused' },
  NOT_EXECUTED: { icon: '○', label: 'Pending',     className: 'stage-pending' },
  ABORTED:      { icon: '⊘', label: 'Aborted',     className: 'stage-aborted' },
};

const StageCard: React.FC<{ stage: PipelineStage }> = ({ stage }) => {
  const meta = STATUS_META[stage.status] ?? STATUS_META.NOT_EXECUTED;
  const duration = stage.status === 'IN_PROGRESS'
    ? formatElapsed(stage.startTimeMillis)
    : formatDuration(stage.durationMillis);

  const isFuture = stage.id.startsWith('future-');

  // Future (template) stages → gray pending. Active stages that failed/aborted/are
  // running → their own colour. Everything else (SUCCESS, instant NOT_EXECUTED, etc.)
  // that actually ran → green.
  const stageClass = (() => {
    if (stage.status === 'FAILED')      return 'stage-failed';
    if (stage.status === 'UNSTABLE')    return 'stage-unstable';
    if (stage.status === 'ABORTED')     return 'stage-aborted';
    if (stage.status === 'IN_PROGRESS') return 'stage-running';
    if (stage.status === 'PAUSED')      return 'stage-paused';
    if (isFuture)                       return 'stage-pending';
    return 'stage-success';
  })();

  return (
    <div className={`pipeline-stage ${stageClass}`}>
      <div className="stage-icon">
        {stage.status === 'IN_PROGRESS'
          ? <span className="stage-spinner" />
          : meta.icon}
      </div>
      <div className="stage-name" title={stage.name}>{stage.name}</div>
      <div className="stage-duration">{duration}</div>
    </div>
  );
};

export const PipelineModal: React.FC<PipelineModalProps> = ({ prNumber, prTitle, onClose }) => {
  const [build, setBuild] = useState<PipelineBuild | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);
  const getToken = useAccessToken();

  const fetchPipeline = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchApi<PipelineBuild>(
        `/api/mergequeue/pipeline?prNumber=${prNumber}`,
        getToken
      );
      setBuild(data);
      setFetchedAt(new Date());
    } catch (err: any) {
      setError(err?.message || 'Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, [prNumber, getToken]);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const overallMeta = build ? (STATUS_META[build.status] ?? STATUS_META.NOT_EXECUTED) : null;
  const overallDuration = build
    ? (build.status === 'IN_PROGRESS' ? formatElapsed(build.startTimeMillis) : formatDuration(build.durationMillis))
    : null;

  return (
    <div className="pipeline-modal-overlay" onClick={onClose}>
      <div className="pipeline-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="pipeline-modal-header">
          <div className="pipeline-modal-title">
            <span className="pipeline-title-pr">PR #{prNumber}</span>
            <span className="pipeline-title-name" title={prTitle}>{prTitle}</span>
          </div>
          <button className="pipeline-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Sub-header: build meta */}
        {build && overallMeta && (
          <div className="pipeline-modal-meta">
            <a
              href={build.buildUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="pipeline-build-link"
            >
              Build #{build.buildNumber} ↗
            </a>
            <span className={`pipeline-overall-status ${overallMeta.className}`}>
              {overallMeta.icon} {overallMeta.label}
            </span>
            <span className="pipeline-elapsed">
              {build.status === 'IN_PROGRESS' ? 'Running for' : 'Took'} {overallDuration}
            </span>
            {fetchedAt && (
              <span className="pipeline-fetched-at">
                as of {fetchedAt.toLocaleTimeString()}
              </span>
            )}
            <button
              className="pipeline-refresh-btn"
              onClick={fetchPipeline}
              disabled={loading}
              title="Refresh pipeline status"
            >
              {loading ? '⟳' : '↻'} Refresh
            </button>
          </div>
        )}

        {/* Body */}
        <div className="pipeline-modal-body">
          {loading && !build && (
            <div className="pipeline-loading">
              <div className="dx-spinner dx-spinner--lg" />
              <p>Loading pipeline stages…</p>
            </div>
          )}

          {error && (
            <div className="pipeline-error">
              <p>❌ {error}</p>
              <button className="pipeline-retry-btn" onClick={fetchPipeline}>Retry</button>
            </div>
          )}

          {build && build.stages.length === 0 && (
            <div className="pipeline-empty">No stages found for this build.</div>
          )}

          {build && build.stages.length > 0 && (
            <div className="pipeline-stages-container">
              {build.stages.map((stage, i) => (
                <React.Fragment key={stage.id}>
                  <StageCard stage={stage} />
                  {i < build.stages.length - 1 && (() => {
                    const from = build.stages[i];
                    const fromDone = !from.id.startsWith('future-') &&
                      from.status !== 'FAILED' && from.status !== 'UNSTABLE' &&
                      from.status !== 'ABORTED' && from.status !== 'IN_PROGRESS' &&
                      from.status !== 'PAUSED';
                    return <div className={`pipeline-connector${fromDone ? '' : ' connector-pending'}`} />;
                  })()}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
